import { Button } from "@/components/ui/button";
import { EllipsisVerticalIcon, RefreshCwIcon, SearchIcon } from "lucide-react";
import { FilterInput } from "./FilterInput";
import { PageSizeSelect } from "./PageSizeSelect";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface Props {
  filterValue: string;
  totalRecord: number;
  pageSizeOptions?: number[];
  defaultPageSize: number;
  onFilterChange: (value: string) => void;
  onPageSizeChange: (value: number) => void;
  onRefreshClick: () => void;
  isLoading: boolean;
  extraHeader?: React.ReactNode;
  hidePageSizeSelector?: boolean;
}

export const AxDataTableHeader: React.FC<Props> = ({
  filterValue,
  totalRecord,
  pageSizeOptions,
  defaultPageSize,
  onFilterChange,
  onPageSizeChange,
  onRefreshClick,
  isLoading,
  extraHeader,
  hidePageSizeSelector = false,
}) => {
  const isMobile = useIsMobile();
  return (
    <div className="flex flex-col gap-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground animate-in fade-in slide-in-from-left-5 duration-500">&nbsp;</div>
        <div className="flex items-center gap-2">
          {!isMobile && extraHeader}
          {isMobile && extraHeader && (
            <Popover>
              <PopoverTrigger>
                <Button variant="outline" size="sm" className="transition-all duration-200 hover:scale-105">
                  <EllipsisVerticalIcon className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className={cn("p-4 w-full", isMobile ? "flex flex-col gap-2" : "flex items-center gap-2")}
                side="bottom"
              >
                {extraHeader}
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-5 duration-500">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <FilterInput
            value={filterValue}
            onChange={onFilterChange}
            className="pl-9 w-full transition-all duration-200 focus-within:ring-2 focus-within:ring-primary/20"
          />
        </div>
        {!hidePageSizeSelector && pageSizeOptions && (
          <PageSizeSelect
            totalRecord={totalRecord}
            pageSizeOptions={pageSizeOptions}
            onPageSizeChange={onPageSizeChange}
            isLoading={isLoading}
            defaultPageSize={defaultPageSize}
          />
        )}
        <Button
          onClick={onRefreshClick}
          disabled={isLoading}
          size="sm"
          variant="outline"
          className={cn("h-[38px] w-[38px] p-0 transition-all duration-300")}
        >
          <RefreshCwIcon className="h-4 w-4" />
        </Button>
        {isLoading && (
          <div className="animate-in fade-in duration-300">
            <LoadingSmall />
          </div>
        )}
      </div>
    </div>
  );
};
